import { useRef, type ReactNode } from 'react';

import type { ApiFailure } from '../../lib/http/errors';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../ui/alert-dialog';
import { Spinner } from '../../ui/spinner';
import { ErrorState } from '../state/ErrorState';
import { st } from '../state/messages';
import { ft } from './messages';

export function ConfirmDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  pending: boolean;
  failure: ApiFailure | null;
  /** Returning the mutation's promise (`mutateAsync`) hands the close to this dialog: it closes
   *  once the promise resolves, stays open on a rejection (the caller's `failure` renders it),
   *  and refuses a second confirm while the first is in flight. A `void` return leaves the close
   *  to the caller. */
  onConfirm: () => void | Promise<unknown>;
  destructive?: boolean;
  trigger?: ReactNode;
  children?: ReactNode;
  // A caller whose confirm needs a precondition this dialog cannot see (a
  // required choice made in its own `children`) gates the button's own
  // visual and `aria-disabled` state with this, on top of `pending`.
  // `onConfirm` does not fire while it is set.
  confirmDisabled?: boolean;
}): ReactNode {
  const variant = props.destructive ? 'destructive' : 'default';
  const confirmBlocked = props.pending || (props.confirmDisabled ?? false);
  const inFlight = useRef(false);
  function confirm(): void {
    // `pending` only reaches this button after React Query's notify tick and a render; this ref
    // closes that gap against a double click.
    if (inFlight.current) return;
    const settled = props.onConfirm();
    if (settled === undefined) return;
    inFlight.current = true;
    void settled
      .then(
        () => props.onOpenChange(false),
        // The caller's `failure` already renders the rejection; the dialog stays open on it.
        () => undefined,
      )
      .finally(() => {
        inFlight.current = false;
      });
  }
  return (
    <AlertDialog open={props.open} onOpenChange={props.onOpenChange}>
      {props.trigger !== undefined && (
        <AlertDialogTrigger asChild>{props.trigger}</AlertDialogTrigger>
      )}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{props.title}</AlertDialogTitle>
          <AlertDialogDescription>{props.description}</AlertDialogDescription>
        </AlertDialogHeader>
        {props.failure !== null && <ErrorState failure={props.failure} />}
        {props.children}
        <AlertDialogFooter>
          {/* `disabled` (not `aria-disabled`) removes a button from the tab
           * order; with both cancel and confirm disabled during `pending`
           * the trap has nothing to hold, so Tab only ever reaches Radix's
           * own focus guards. `aria-disabled` plus a click-time guard keeps
           * each button reachable while blocking the action it names. */}
          <AlertDialogCancel
            aria-disabled={props.pending}
            className={props.pending ? 'pointer-events-none opacity-50' : undefined}
            onClick={(event) => {
              if (props.pending) event.preventDefault();
            }}
          >
            {props.cancelLabel ?? ft('cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            variant={variant}
            aria-disabled={confirmBlocked}
            // The status role's own label would otherwise be read into this
            // button's name-from-content ("CarregandoRemover"); an explicit
            // name keeps it just the label while the spinner still announces.
            aria-label={props.confirmLabel}
            className={confirmBlocked ? 'pointer-events-none opacity-50' : undefined}
            onClick={(event) => {
              // Radix's own close would fire before the mutation settles and
              // hide the failure it produced; `confirm` closes on success.
              event.preventDefault();
              // `aria-disabled` + `pointer-events-none` block a mouse click but
              // not a keyboard-triggered one (Enter/Space bypass pointer-events
              // entirely), so this guard is the only thing stopping a keyboard
              // user from firing a gated confirm.
              if (confirmBlocked) return;
              confirm();
            }}
          >
            {props.pending && <Spinner aria-label={st('loading')} />}
            {props.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
